import React, { Fragment, SyntheticEvent, useState } from 'react';
import styled from 'styled-components';
import { Colors } from '../../styles/colors';
import { Icon } from './Icon';
import { deepCopy } from '../../utils/deepUtils';
import xlsx from 'xlsx';
import { FlexboxCenter, FlexboxVertical } from './Layout';
import { useShowConfirmationPopup } from '../general/ConfirmationPopup';
import { useShowAlertPopup } from '../..';

const Grid = styled.div<{ hasError: boolean }>`
  display: grid;
  width: 100%;
  color: ${Colors.grayDark};
  font-size: 0.875rem;
  font-family: 'Akkurat';
  background: ${Colors.background};
  padding: 1rem;
  border: ${({ hasError }) => (hasError ? `1px solid ${Colors.error}` : 'none')};
  overflow: scroll;
`;
const GridRow = styled.div<{ isActive: boolean }>`
  display: grid;
  width: 100%;
  grid-template-columns: 12.5rem repeat(3, 5rem) 10rem repeat(4, 5.5rem) repeat(2, 2rem);
  background: ${({ isActive }) => (isActive ? Colors.primaryHighlight : 'transparent')};
  color: ${Colors.grayDark};
  padding: 0 0.5rem;
`;
const GridLine = styled.div`
  background: ${Colors.gray};
  height: 1px;
  width: 100%;
`;

const GridInputItem = styled.input<{ isActive: boolean }>`
  background: transparent;
  border: none;
  width: 90%;
  color: ${Colors.grayDark};
  outline: none;
  align-self: flex-start;
  padding: 0.3rem 0;
  border-bottom: ${({ isActive }) => (isActive ? `1px solid ${Colors.primary}` : 'none')};
`;
const GridIcon = styled(FlexboxCenter)<{ isActive: boolean }>`
  color: ${({ isActive }) => (isActive ? `white` : Colors.primary)};
  background: ${({ isActive }) => (isActive ? Colors.primary : 'transparent')};
  outline: none;
  cursor: pointer;
  width: 1.5rem;
  height: 1.5rem;
  padding: 0.5rem;
  border-radius: 2px;
`;
const GridItem = styled(FlexboxCenter)`
  border: none;
  width: 100%;
  padding: 1rem 0rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const UploadWrapper = styled.div`
  padding: 1rem 0;
`;
const GridLabel = styled.label`
  color: ${Colors.primary};
  cursor: pointer;
  text-decoration: underline;
`;
const GridFileInput = styled.input`
  display: none;
`;

const Headline = styled.div`
  margin-bottom: 1rem;
  font-size: 1rem;
`;
const AddRowButton = styled.div`
  color: ${Colors.primary};
  cursor: pointer;
  padding-top: 1.75rem;
`;
const ErrorMessage = styled.p`
  color: ${Colors.error};
  font-size: 0.75rem;
  padding-left: 1rem;
`;

export const Table = (props: { items: any[]; setItems: Function; hasError: boolean }) => {
  const [activeRow, setActiveRow] = useState<number>(-1);
  const [activeItem, setActiveItem] = useState(null);
  const fileInputRef = React.useRef(null);
  const showConfirmationPopup = useShowConfirmationPopup();
  const showNotificationBanner = useShowAlertPopup();

  const onFileInputChange = async (event: any) => {
    if (event.target.files.length > 0) {
      try {
        const sheet: xlsx.WorkSheet = await readExcelFile(event.target.files[0]);
        const data = sheetToArray(sheet);
        if (activeRow != -1) cancelRowEdit();
        props.setItems(dataArrayToAddressArray(data));
      } catch (err) {
        showNotificationBanner({
          error: true,
          renderContent: () => {
            return <>Die importierte Excel-Tabelle entspricht nicht dem erwarteten Format.</>;
          },
        });
      }
    }
  };

  const readExcelFile = (file: Blob) => {
    return new Promise<xlsx.WorkSheet>((resolve, reject) => {
      let reader = new FileReader();
      reader.onloadend = (event) => {
        const arrayBuffer = reader.result;
        const workbook = xlsx.read(arrayBuffer, { type: 'array' });
        const firstSheet = workbook.Sheets[Object.keys(workbook.Sheets)[0]];
        resolve(firstSheet);
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  };

  const sheetToArray = (sheet: xlsx.WorkSheet, maxColumns = 11) => {
    const jsonSheet: any[] = xlsx.utils.sheet_to_json(sheet);
    jsonSheet.shift();
    jsonSheet.forEach((element: any) => {
      if ('__EMPTY_2' in element) {
        element['__EMPTY_1'] += element['__EMPTY_2'];
        delete element['__EMPTY_2'];
      }
    });
    return jsonSheet.map((row: []) => Object.values(row).slice(0, maxColumns));
  };

  const dataArrayToAddressArray = (data: any[][]) => {
    return data.map((element) => ({
      street: ""+element[0],
      streetNumber: ""+element[1],
      zip: ""+element[2],
      city: ""+element[3],
      consumption: ""+element[4],
      unit: ""+element[5],
      type: ""+element[6],
      supplier: ""+element[7],
      thermalLine: ""+element[8],
    }));
  };

  const addEmptyRow = () => {
    if (activeRow != -1 && isItemEmpty(activeItem)) return;
    const emptyAddress = { street: '', streetNumber: '', zip: '', city: '' };
    const rowIndex = props.items.length;
    props.setItems(props.items.concat(emptyAddress));
    setActiveItem(emptyAddress);
    setActiveRow(rowIndex);
  };

  const isItemEmpty = (item: Object) => {
    for (let value of Object.values(item)) {
      if (String(value).trim() != '') return false;
    }
    return true;
  };

  const cancelRowEdit = () => {
    const isNewRow = isItemEmpty(props.items[activeRow]);
    if (isItemEmpty(activeItem) || isNewRow) {
      deleteRow(activeRow);
    }
    setActiveRow(-1);
    setActiveItem(null);
  };

  const deleteRow = (...toDelete: number[]) => {
    const items = props.items.filter((item, i) => toDelete.indexOf(i) == -1);
    props.setItems(items);
  };

  const showDeletePopup = (toDelete: number) => {
    showConfirmationPopup({
      open: true,
      actionButtonLabel: 'Löschen',
      closeFn() {
        showConfirmationPopup(null);
      },
      actionFn() {
        if (activeRow != -1 && isItemEmpty(activeItem)) {
          //NOTE could call cancelRowEdit but had problem with state updates
          deleteRow(toDelete, activeRow);
        } else {
          deleteRow(toDelete);
        }
        setActiveItem(null);
        setActiveRow(-1);
        showConfirmationPopup(null);
      },
      renderer() {
        return <>Wollen sie die Addresse "{props.items[toDelete].street}" wirklick löschen?</>;
      },
    });
  };

  const saveRow = (index: number) => {
    if (isItemEmpty(activeItem)) {
      deleteRow(activeRow);
    } else {
      const items = props.items.map((address, i) => (i == index ? activeItem : address));
      props.setItems(items);
    }
    setActiveItem(null);
    setActiveRow(-1);
  };

  const editRow = (index: number) => {
    setActiveItem(deepCopy(props.items[index]));
    setActiveRow(index);
  };

  return (
    <>
      <Grid hasError={props.hasError}>
        <GridRow isActive={false}>
          <Headline>Straße</Headline>
          <Headline>Hsnr.</Headline>
          <Headline>PLZ</Headline>
          <Headline>Ort</Headline>
          <Headline>Jahresverbrauch</Headline>
          <Headline>Einheit</Headline>
          <Headline>Energieart</Headline>
          <Headline>Versorger</Headline>
          <Headline>Wärmetrasse</Headline>
        </GridRow>
        <GridLine />
        {props.items &&
        props.items.map((address, index) => {
          const isActive = index == activeRow;
          return (
            <Fragment key={index}>
              <GridRow isActive={isActive}>
                <GridItem>
                  <GridInputItem
                    isActive={isActive}
                    onChange={(event) => setActiveItem({ ...activeItem, street: event.target.value })}
                    value={isActive ? activeItem.street : address.street}
                    readOnly={!isActive} />
                </GridItem>
                <GridItem>
                  <GridInputItem
                    isActive={isActive}
                    onChange={(event) => setActiveItem({ ...activeItem, streetNumber: event.target.value })}
                    value={isActive ? activeItem.streetNumber : address.streetNumber}
                    readOnly={!isActive} />
                </GridItem>
                <GridItem>
                  <GridInputItem
                    isActive={isActive}
                    onChange={(event) => setActiveItem({ ...activeItem, zip: event.target.value })}
                    value={isActive ? activeItem.zip : address.zip}
                    readOnly={!isActive} />
                </GridItem>
                <GridItem>
                  <GridInputItem
                    isActive={isActive}
                    onChange={(event) => setActiveItem({ ...activeItem, city: event.target.value })}
                    value={isActive ? activeItem.city : address.city}
                    readOnly={!isActive} />
                </GridItem>
                <GridItem>
                  <GridInputItem
                    isActive={isActive}
                    onChange={(event) => setActiveItem({ ...activeItem, consumption: event.target.value })}
                    value={isActive ? activeItem.consumption : address.consumption}
                    readOnly={!isActive} />
                </GridItem>
                <GridItem>
                  <GridInputItem
                    isActive={isActive}
                    onChange={(event) => setActiveItem({ ...activeItem, unit: event.target.value })}
                    value={isActive ? activeItem.unit : address.unit}
                    readOnly={!isActive} />
                </GridItem>
                <GridItem>
                  <GridInputItem
                    isActive={isActive}
                    onChange={(event) => setActiveItem({ ...activeItem, type: event.target.value })}
                    value={isActive ? activeItem.type : address.type}
                    readOnly={!isActive} />
                </GridItem>
                <GridItem>
                  <GridInputItem
                    isActive={isActive}
                    onChange={(event) => setActiveItem({ ...activeItem, supplier: event.target.value })}
                    value={isActive ? activeItem.supplier : address.supplier}
                    readOnly={!isActive} />
                </GridItem>
                <GridItem>
                  <GridInputItem
                    isActive={isActive}
                    onChange={(event) => setActiveItem({ ...activeItem, thermalLine: event.target.value })}
                    value={isActive ? activeItem.thermalLine : address.thermalLine}
                    readOnly={!isActive} />
                </GridItem>
                {!isActive && (
                  <>
                    <GridItem>
                      <GridIcon isActive={isActive} onClick={() => editRow(index)}>
                        <Icon icon={'edit'} size={24} />
                      </GridIcon>
                    </GridItem>

                    <GridItem>
                      <GridIcon isActive={isActive} onClick={() => showDeletePopup(index)}>
                        <Icon icon={'trash'} size={24} />
                      </GridIcon>
                    </GridItem>
                  </>
                )}
                {isActive && (
                  <>
                    <GridItem>
                      <GridIcon isActive={isActive} onClick={cancelRowEdit}>
                        <Icon icon={'close'} size={12} />
                      </GridIcon>
                    </GridItem>
                    <GridItem>
                      <GridIcon isActive={isActive} onClick={() => saveRow(index)}>
                        <Icon icon={'check'} size={14} />
                      </GridIcon>
                    </GridItem>
                  </>
                )}
              </GridRow>
              <GridLine />
            </Fragment>
          );
        })}
        <div>
          <AddRowButton onClick={addEmptyRow}>+ Zeile hinzufügen</AddRowButton>
        </div>
        <UploadWrapper>
          <GridLabel htmlFor="address-file" onClick={() => fileInputRef.current.click()}>
            .xlsx oder .xlsm importieren
          </GridLabel>
          <GridFileInput
            type="file"
            accept=".xlsx, .xlsm"
            name="address-file"
            ref={fileInputRef}
            onChange={(event) => {
              onFileInputChange(event);
              event.target.value = null;
            }}
          />
        </UploadWrapper>
      </Grid>
      {props.hasError && <ErrorMessage>Eine oder mehrere Zeilen sind unvollständig</ErrorMessage>}
    </>
  );
};
